import { CLUB_CATEGORY_LABELS, MAX_CLUBS_PER_BAG } from '@golf/common';
import { Check, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useHaptics } from '../../hooks/useHaptics.ts';
import { ApiError } from '../../lib/api.ts';
import type { Bag, Club } from '../../lib/equipment.ts';
import { clubDisplayLabel, createBag, setBagClubs, updateBag } from '../../lib/equipment.ts';

interface Props {
    bag: Bag | null;
    clubs: Club[];
    onClose: () => void;
    onSaved: () => void;
    /** Supprimer ce sac. Absent à la création. */
    onArchive?: (bag: Bag) => void;
}

export function BagEditor({ bag, clubs, onClose, onSaved, onArchive }: Props) {
    const [name, setName] = useState(bag?.name ?? '');
    const [description, setDescription] = useState(bag?.description ?? '');
    const [selected, setSelected] = useState<string[]>(bag?.clubs.map((c) => c.id) ?? []);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const { vibrate } = useHaptics();

    const full = selected.length >= MAX_CLUBS_PER_BAG;

    const toggle = (id: string): void => {
        setSelected((prev) => {
            if (prev.includes(id)) {
                vibrate('tap');
                return prev.filter((x) => x !== id);
            }
            if (prev.length >= MAX_CLUBS_PER_BAG) {
                vibrate('warning');
                setError(`Un sac ne peut pas dépasser ${MAX_CLUBS_PER_BAG} clubs. Retirez-en un d'abord.`);
                return prev;
            }
            vibrate('tap');
            setError(null);
            return [...prev, id];
        });
    };

    const submit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const id = bag ? bag.id : (await createBag({ name: name.trim(), description: description.trim() || null })).bag.id;
            if (bag) await updateBag(bag.id, { name: name.trim(), description: description.trim() || null });
            // La composition part d'un bloc : le serveur applique les retraits
            // avant les ajouts, dans une transaction. C'est ce qui permet
            // d'échanger un club contre un autre dans un sac déjà plein.
            await setBagClubs(id, selected);
            onSaved();
            onClose();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Enregistrement impossible');
        } finally {
            setBusy(false);
        }
    };

    // Groupés par famille, du driver au putter : c'est l'ordre du sac.
    const byCategory = clubs.reduce<Record<string, Club[]>>((acc, c) => {
        (acc[c.category] ??= []).push(c);
        return acc;
    }, {});

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
            <div className="flex max-h-[92dvh] w-full max-w-lg flex-col rounded-t-box bg-base-200 sm:rounded-box">
                <div className="flex items-center justify-between px-4 py-3">
                    <h2 className="text-lg font-bold">{bag ? 'Modifier le sac' : 'Nouveau sac'}</h2>
                    <button type="button" className="btn btn-ghost btn-square" onClick={onClose} aria-label="Fermer">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={(e) => void submit(e)} className="flex min-h-0 flex-1 flex-col">
                    <div className="space-y-3 px-4">
                        <label className="form-control w-full">
                            <span className="label-text mb-1 block text-sm">Nom *</span>
                            <input
                                className="input input-bordered w-full" required maxLength={60} autoFocus
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Parcours, Practice, Hiver…"
                            />
                        </label>
                        <label className="form-control w-full">
                            <span className="label-text mb-1 block text-sm">Description</span>
                            <textarea
                                className="textarea textarea-bordered w-full" rows={2} maxLength={500}
                                value={description} onChange={(e) => setDescription(e.target.value)}
                                placeholder="À quoi sert cette configuration : type de parcours, météo, saison…"
                            />
                        </label>

                        <div className="flex items-center justify-between pt-1">
                            <span className="text-sm font-semibold">Composition</span>
                            <span className={`badge ${full ? 'badge-warning' : 'badge-ghost'}`}>
                                {selected.length} / {MAX_CLUBS_PER_BAG}
                            </span>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-2">
                        {clubs.length === 0 && (
                            <p className="py-6 text-center text-sm opacity-60">
                                Aucun club dans votre matériel. Ajoutez-en d'abord.
                            </p>
                        )}
                        {Object.entries(byCategory).map(([cat, list]) => (
                            <div key={cat}>
                                <div className="mb-1 text-xs font-semibold uppercase tracking-wider opacity-50">
                                    {CLUB_CATEGORY_LABELS[cat as keyof typeof CLUB_CATEGORY_LABELS]}
                                </div>
                                <div className="space-y-1">
                                    {list.map((c) => {
                                        const on = selected.includes(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => toggle(c.id)}
                                                aria-pressed={on}
                                                className={`flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-left transition-colors ${
                                                    on ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                                } ${!on && full ? 'opacity-40' : ''}`}
                                            >
                                                <span className={`flex size-5 shrink-0 items-center justify-center rounded border ${
                                                    on ? 'border-primary-content bg-primary-content/20' : 'border-base-content/30'
                                                }`}>
                                                    {on && <Check size={14} />}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-sm">
                                                    {clubDisplayLabel(c)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="safe-bottom space-y-2 border-t border-base-300 px-4 pt-3">
                        {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}
                        <div className="flex gap-2">
                            <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Annuler</button>
                            <button type="submit" className="btn btn-primary flex-1 tap-target" disabled={busy}>
                                {busy ? <span className="loading loading-spinner" /> : 'Enregistrer'}
                            </button>
                        </div>
                        {/* Le geste destructeur demande d'avoir ouvert la bonne
                            fiche — même règle que pour un club. */}
                        {bag && onArchive && (
                            <button type="button"
                                onClick={() => { onArchive(bag); onClose(); }}
                                className="btn btn-ghost btn-sm mx-auto flex gap-2 font-semibold"
                                style={{ color: 'var(--brique-bord)' }}>
                                <Trash2 size={15} aria-hidden="true" /> Supprimer ce sac
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
